import React, { useState, useRef } from 'react';

interface FileUploadProps {
  label: string;
  onChange: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in bytes
  maxFiles?: number;
  error?: string;
  className?: string;
  messages?: {
    error?: string;
    success?: string;
    removed?: string;
    max?: string;
    size?: string;
    type?: string;
  };
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  onChange,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  multiple = true,
  maxSize = 5 * 1024 * 1024, // 5MB default
  maxFiles = 5,
  error,
  className = '',
  messages = {},
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const handleFilesSelected = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    
    const newFiles = Array.from(selectedFiles);
    const validFiles = newFiles.filter(file => file.size <= maxSize);
    
    if (validFiles.length === 0) {
      if (messages.size) {
        console.error(messages.size);
      }
      return;
    }
    
    const combinedFiles = multiple 
      ? [...files, ...validFiles].slice(0, maxFiles) 
      : validFiles.slice(0, 1);
    
    if (combinedFiles.length < newFiles.length && messages.max) {
      console.error(messages.max);
    }
    
    setFiles(combinedFiles);
    onChange(combinedFiles);
  };
  
  const handleFileRemove = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
    onChange(updatedFiles);
    if (messages.removed) {
      console.log(messages.removed);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    handleFilesSelected(droppedFiles);
  };
  
  return (
    <div className={`mb-4 ${className}`}>
      <label className="block text-sm font-medium text-primary-700 mb-1">
        {label}
      </label>
      
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-md p-4 text-center cursor-pointer
          transition duration-150 ease-in-out
          ${isDragging ? 'border-secondary-500 bg-secondary-50' : 'border-gray-300 hover:border-secondary-500'}
          ${error ? 'border-red-500 bg-red-50' : ''}
        `}
      >
        <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        
        <p className="mt-1 text-sm text-gray-600">
          {multiple 
            ? `Drag and drop files here, or click to select files`
            : `Drag and drop a file here, or click to select a file`}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {multiple 
            ? `Up to ${maxFiles} files, max ${formatFileSize(maxSize)} each`
            : `Max size: ${formatFileSize(maxSize)}`}
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
      </div>
      
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      
      {files.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-200 border border-gray-200 rounded-md">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
              <div className="w-0 flex-1 flex items-center">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                </svg>
                <span className="ml-2 flex-1 w-0 truncate text-gray-900">
                  {file.name}
                </span>
              </div>
              <div className="ml-4 flex-shrink-0 flex space-x-4">
                <span className="text-xs text-gray-900">
                  {formatFileSize(file.size)}
                </span>
                <button
                  type="button"
                  className="text-secondary-600 hover:text-secondary-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileRemove(index);
                  }}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;